<template>
  <div data-aos="fade-up" class="timeline-item">
    <div
      class="timeline-content"
      :class="{
        'order-3': isContentRight,
        'padding-left': isContentRight,
        'padding-right': !isContentRight,
      }">
      <div
        class="card"
        :class="{
          light: !isDarkMode,
        }">
        <h2 class="title">{{ title }}</h2>
        <div class="mb-2 company">{{ company }}</div>
        <div>{{ description }}</div>
        <div v-if="url" class="mt-2 mb-2">
          <a
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-custom btn-sm project-link"
            :class="{
              'btn-outline-dark-custom': isDarkMode,
              'btn-outline-light-custom': !isDarkMode,
            }">
            View Project
          </a>
        </div>
        <div class="mt-2 date">{{ date }}</div>
      </div>
    </div>
    <div class="timeline-middle">
      <font-awesome-icon
        :icon="['fas', icon ?? 'briefcase']"
        size="xl"
        class="icon"
        :class="{
          light: !isDarkMode,
        }" />
    </div>
    <div class="timeline-date" :class="{'order-1': isContentRight}">
      <h3 class="date">{{ date }}</h3>
    </div>
  </div>
</template>

<script setup lang="ts">
import {defineProps, ref, watch} from 'vue';
import {useStore} from 'vuex';

defineProps<{
  title: string;
  company: string;
  date: string;
  description: string;
  icon?: string;
  isContentRight: boolean;
  url?: string;
}>();

const store = useStore();
const isDarkMode = ref(store.state.isDarkMode);

watch(
  () => store.state.isDarkMode,
  newValue => {
    isDarkMode.value = newValue;
  },
);
</script>

<style lang="scss" scoped>
.timeline-item {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;

  .timeline-content {
    width: 45%;
    order: 1;

    .card {
      color: var(--dark-mode-text);
      padding: 20px;
      background-color: var(--dark-mode-secondary-bg);
      color: var(--dark-mode-text);
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      border-radius: 8px;

      &.light {
        background-color: var(--light-mode-secondary-bg);
        color: var(--light-mode-text);
      }

      .title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 10px;
      }

      .company {
        font-weight: 600;
      }

      .date {
        display: none;
      }

      .project-link {
        text-decoration: none;

        &.btn-outline-dark-custom {
          color: #ccc;
          border-color: #ccc;
        }

        &.btn-outline-light-custom {
          color: #000000;
          border-color: #000000;
        }

        &:hover {
          color: white;
          background-color: #000000;
          border-color: #000000;
        }
      }
    }
  }

  .timeline-date {
    width: 45%;
    padding: 20px;
    order: 3;
  }

  .timeline-middle {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 10%;
    z-index: 1;
    order: 2;

    .icon {
      background-color: var(--dark-mode-secondary-bg);
      border: 2px solid #ddd;
      border-radius: 50%;
      padding: 10px;
      color: var(--dark-mode-text);

      &.light {
        background-color: var(--light-mode-secondary-bg);
        color: var(--light-mode-text);
      }
    }
  }
}

.date,
.location {
  font-size: 14px;
}

// Swap the content and date
.order-1 {
  order: 1;
  text-align: right;
}
.order-3 {
  order: 3;
}

@media (min-width: 768px) {
  .timeline-item {
    .timeline-content {
      &.padding-right {
        padding-right: 20px;
      }

      &.padding-left {
        padding-left: 20px;
      }
    }
  }
}

@media (max-width: 768px) {
  .timeline-item {
    .timeline-content {
      width: 100%;
      padding-bottom: 20px;

      .card .date {
        display: block;
      }
    }

    .timeline-date {
      display: none;
    }

    .timeline-middle {
      display: none;
    }
  }
}
</style>
